import { FC } from 'react';
import { FormFlkRule } from 'src/shared/store/projects/types';
import { Button } from 'src/shared/ui';

type FlkRulesBuilderProps = {
  rules: FormFlkRule[];
  availableColumns?: string[];
  onChange: (rules: FormFlkRule[]) => void;
};

const createRuleId = () => `flk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createDefaultRule = (): FormFlkRule => ({
  id: createRuleId(),
  name: '',
  expression: '',
  message: '',
  level: 'error',
  scope: 'cell',
  targetColumn: '',
  priority: 1,
  enabled: true,
  config: {},
});

const normalizeRule = (rule: FormFlkRule, fallbackIndex: number): FormFlkRule => ({
  id: rule.id || `flk_${fallbackIndex + 1}`,
  name: rule.name || '',
  expression: rule.expression || '',
  message: rule.message || '',
  level: (rule.level || 'error').toLowerCase(),
  scope: (rule.scope || 'cell').toLowerCase(),
  targetColumn: rule.targetColumn || '',
  priority:
    Number.isFinite(rule.priority) && Number(rule.priority) > 0
      ? Math.trunc(Number(rule.priority))
      : fallbackIndex + 1,
  enabled: rule.enabled !== false,
  config: { ...(rule.config || {}) },
});

const examples = [
  "amount >= 0 and (status = 'approved' or status = 'review')",
  "not is_empty(code) and len(code) = 6",
  "matches(code, '^[A-Z]{2}-\\\\d{3}$')",
];

export const FlkRulesBuilder: FC<FlkRulesBuilderProps> = ({
  rules,
  availableColumns = [],
  onChange,
}) => {
  const safeRules = rules.map((rule, index) => normalizeRule(rule, index));
  const columnSuggestions = Array.from(
    new Set(availableColumns.map(column => column.trim()).filter(Boolean)),
  );

  const updateRules = (nextRules: FormFlkRule[]) => {
    onChange(
      nextRules.map((rule, index) => ({
        ...rule,
        priority: index + 1,
      })),
    );
  };

  const updateRule = (index: number, updater: (rule: FormFlkRule) => FormFlkRule) => {
    const next = [...safeRules];
    next[index] = normalizeRule(updater(next[index]), index);
    updateRules(next);
  };

  const addRule = () => updateRules([...safeRules, createDefaultRule()]);
  const removeRule = (index: number) => updateRules(safeRules.filter((_, i) => i !== index));

  return (
    <div className="mt-5 rounded border border-blue-400 p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs opacity-70 dark:text-white">
          Формально-логический контроль (ФЛК)
        </div>
        <Button type="button" size="small" variant="green" onClick={addRule}>
          Добавить правило ФЛК
        </Button>
      </div>

      <div className="mb-3 text-xs text-grey-300">
        Правило считается пройденным, если выражение истинно. Поддерживаются сравнения
        ` = `, ` != `, ` &gt; `, ` &gt;= `, ` &lt; `, ` &lt;= `, логические операторы
        ` and `, ` or `, ` not `, скобки, строковые литералы в кавычках, `true`/`false`,
        `null`, а также функции `is_empty`, `len`, `contains`, `starts_with`, `ends_with`,
        `matches`.
      </div>

      <div className="mb-4 rounded border border-violet-300 bg-grey-200 p-3 text-extra-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white">
        <div className="mb-2 opacity-70">Примеры выражений:</div>
        <div className="space-y-1 font-mono">
          {examples.map(example => (
            <div key={example}>{example}</div>
          ))}
        </div>
      </div>

      {!!columnSuggestions.length && (
        <datalist id="flk-rule-columns">
          {columnSuggestions.map(column => (
            <option key={`flk-column-${column}`} value={column} />
          ))}
        </datalist>
      )}

      {!safeRules.length && (
        <div className="text-xs text-grey-300">
          Правила ФЛК не настроены. Добавьте правило для проверки значений при сохранении формы.
        </div>
      )}

      <div className="space-y-4">
        {safeRules.map((rule, index) => (
          <div
            key={rule.id || `flk-rule-${index}`}
            className="rounded border border-violet-400 p-3"
          >
            <div className="mb-3 grid grid-cols-12 gap-2">
              <div className="col-span-1 flex items-center justify-center text-xs text-grey-300">
                #{index + 1}
              </div>
              <input
                value={rule.name || ''}
                onChange={event =>
                  updateRule(index, current => ({ ...current, name: event.target.value }))
                }
                placeholder="Название правила"
                className="col-span-4 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              />
              <select
                value={rule.level || 'error'}
                onChange={event =>
                  updateRule(index, current => ({ ...current, level: event.target.value }))
                }
                className="col-span-2 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              >
                <option value="error">Ошибка</option>
                <option value="warning">Предупреждение</option>
              </select>
              <select
                value={rule.scope || 'cell'}
                onChange={event =>
                  updateRule(index, current => ({
                    ...current,
                    scope: event.target.value,
                    targetColumn: event.target.value === 'form' ? '' : current.targetColumn,
                  }))
                }
                className="col-span-2 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              >
                <option value="cell">Ячейка</option>
                <option value="form">Форма</option>
              </select>
              {rule.scope !== 'form' ? (
                <input
                  value={rule.targetColumn || ''}
                  onChange={event =>
                    updateRule(index, current => ({ ...current, targetColumn: event.target.value }))
                  }
                  placeholder="Целевая колонка"
                  list="flk-rule-columns"
                  className="col-span-2 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                />
              ) : (
                <div className="col-span-2 rounded border border-violet-300 bg-grey-200 p-2 text-xs text-grey-400 dark:border-violet-900 dark:bg-violet-1200 dark:text-grey-300">
                  область: форма
                </div>
              )}
              <Button
                type="button"
                variant="greyLight"
                size="small"
                classNames="col-span-1"
                onClick={() => removeRule(index)}
              >
                X
              </Button>
            </div>

            <div className="mb-2 grid grid-cols-1 gap-2">
              <input
                value={rule.expression || ''}
                onChange={event =>
                  updateRule(index, current => ({ ...current, expression: event.target.value }))
                }
                placeholder="Выражение, например: amount >= 0 and (status = 'approved' or status = 'review')"
                className="rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              />
              <input
                value={rule.message || ''}
                onChange={event =>
                  updateRule(index, current => ({ ...current, message: event.target.value }))
                }
                placeholder="Пользовательское сообщение (опционально)"
                className="rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              />
            </div>

            <label className="flex items-center gap-2 text-xs dark:text-white">
              <input
                type="checkbox"
                checked={rule.enabled !== false}
                onChange={event =>
                  updateRule(index, current => ({ ...current, enabled: event.target.checked }))
                }
              />
              Включено
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};
